// Wave 3.2 — Reusable SEO head component.
// Injects <title>, <meta name="description">, OpenGraph + Twitter Card,
// and JSON-LD structured data into <head> via useEffect. React-Helmet-free
// (no new dependency). Cleans up on unmount.
import { useEffect } from "react";

function setMetaByName(name, content) {
  if (!content) return null;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}
function setMetaByProperty(property, content) {
  if (!content) return null;
  let el = document.head.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

export default function SEOHead({
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  jsonLd = null,
  noindex = false,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;
    setMetaByName("description", description);
    setMetaByProperty("og:title", title);
    setMetaByProperty("og:description", description);
    setMetaByProperty("og:type", ogType);
    setMetaByProperty("og:url", canonical || window.location.href);
    setMetaByProperty("og:image", ogImage || `${window.location.origin}/website/capabilities/hero.jpg`);
    setMetaByName("twitter:card", "summary_large_image");
    setMetaByName("twitter:title", title);
    setMetaByName("twitter:description", description);
    setMetaByName("twitter:image", ogImage || `${window.location.origin}/website/capabilities/hero.jpg`);
    // R1d.8 — Truthful noindex for 404 pages / unknown category slugs so
    // search engines don't index dead-end SPA routes as real content.
    let robotsEl = null;
    if (noindex) {
      robotsEl = setMetaByName("robots", "noindex, nofollow");
    } else {
      const existing = document.head.querySelector('meta[name="robots"]');
      if (existing) existing.parentNode.removeChild(existing);
    }

    let linkCanonical = document.head.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement("link");
      linkCanonical.setAttribute("rel", "canonical");
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute("href", canonical || window.location.href);

    let ldScript = null;
    if (jsonLd) {
      ldScript = document.createElement("script");
      ldScript.type = "application/ld+json";
      ldScript.setAttribute("data-seo-jsonld", "1");
      ldScript.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(ldScript);
    }
    return () => {
      document.title = prevTitle;
      if (ldScript && ldScript.parentNode) ldScript.parentNode.removeChild(ldScript);
      // Clean up noindex meta on unmount so subsequent pages don't inherit it.
      if (robotsEl && robotsEl.parentNode) robotsEl.parentNode.removeChild(robotsEl);
    };
  }, [title, description, canonical, ogImage, ogType, noindex, JSON.stringify(jsonLd)]);
  return null;
}

// Reusable JSON-LD builders keyed to real ERF data. Every value should
// come from the live /api/public/website/content endpoint — no hardcoding.
export function buildLocalBusinessSchema({ content, states }) {
  return {
    "@context": "https://schema.org",
    "@type": "GeneralContractor",
    "name": "ERF ENTERPRISES LLC",
    "legalName": "ERF ENTERPRISES LLC",
    "founder": "Edwin R. Flamer",
    "description": content.mission_statement || content.hero_subheadline || "",
    "url": window.location.origin + "/",
    "telephone": content.contact_phone_office,
    "email": content.contact_email,
    "logo": window.location.origin + "/website/capabilities/hero.jpg",
    "image": window.location.origin + "/website/capabilities/hero.jpg",
    "areaServed": (states || []).map((s) => ({
      "@type": "State",
      "name": s.state_name,
    })),
    "slogan": content.tagline || "From Foundation to Finish — Built to Last.",
  };
}

export function buildServiceSchema({ service }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": service.name,
    "name": `${service.name} — ERF ENTERPRISES LLC`,
    "description": service.long_description || service.short_description,
    "provider": {
      "@type": "GeneralContractor",
      "name": "ERF ENTERPRISES LLC",
    },
    "url": window.location.href,
  };
}

export function buildServiceAreaSchema({ area }) {
  return {
    "@context": "https://schema.org",
    "@type": "Place",
    "name": area.state_name,
    "description": area.coverage_summary,
    "containedInPlace": {
      "@type": "Country",
      "name": "United States",
    },
    "url": window.location.href,
  };
}
